/**
 * Alert / Banner — interne implementatie aan Aurora-v2 §8.10.
 *
 * Severity-subtle background + matching icon. Geen accent-bars
 * (anders dan Toast). Optionele dismiss-knop. role="alert" voor
 * danger/warning, "status" voor info/success.
 *
 * Bedoeld voor in-flow page-notifications — niet voor transient pop-
 * ups (gebruik Toast daarvoor).
 */
import type { ReactNode } from 'react';
import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  X as XIcon,
} from '@phosphor-icons/react';
import { useAuroraLabels } from '../auroraLabels';
import styles from './Alert.module.css';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  onDismiss?: () => void;
}

const ICONS: Record<AlertTone, typeof CheckCircle> = {
  info: Info,
  success: CheckCircle,
  warning: Warning,
  danger: WarningCircle,
};

export function Alert({ tone = 'info', title, children, onDismiss }: AlertProps) {
  const Icon = ICONS[tone];
  const isAlertRole = tone === 'warning' || tone === 'danger';
  // LOKALE afwijking (issue 294): de naam van de sluitknop komt uit de
  // AuroraLabels-context in plaats van een vaste Engelse "Dismiss".
  const labels = useAuroraLabels();

  return (
    <div
      role={isAlertRole ? 'alert' : 'status'}
      className={[styles.alert, styles[tone]].join(' ')}
    >
      <Icon size={20} weight="fill" className={styles.icon} aria-hidden="true" />
      <div className={styles.body}>
        {title && <strong className={styles.title}>{title}</strong>}
        {children && <div className={styles.text}>{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          aria-label={labels.alertDismiss}
          onClick={onDismiss}
        >
          <XIcon size={16} weight="regular" />
        </button>
      )}
    </div>
  );
}
